import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { deleteShortCode } from 'api/short-codes';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

const DeleteShortCode = ({
    keywordId,
    children,
}: {
    keywordId: number;
    children: React.ReactNode;
}) => {
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();

    const { mutate: handleDelete, isPending: isLoading } = useMutation<
        unknown,
        GivebutterAxiosError
    >({
        mutationFn: () => deleteShortCode(accountId, { keywordId }),
        onSuccess: () => {
            success('Keyword deleted successfully.');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.shortCodes(accountId),
                type: 'all',
            });
        },
        onError: e => {
            error(e.response?.data?.message || 'Unable to delete keyword.');
        },
    });

    return (
        <AlertDialog.Root type="error">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    {isLoading && <Loader />}
                    <AlertDialog.Title>Are you sure?</AlertDialog.Title>
                    <AlertDialog.Description>
                        You will not be able to undo this action.
                    </AlertDialog.Description>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action
                            disabled={isLoading}
                            onClick={e => {
                                e.preventDefault();
                                handleDelete();
                            }}
                        >
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = DeleteShortCode;

const Trigger = AlertDialog.Trigger;
const DeleteShortCodeTrigger = Trigger;

export {
    Root,
    Trigger,
    //
    DeleteShortCode,
    DeleteShortCodeTrigger,
};
